import { IPaginationData } from '../../indigofair/data/IPaginationData';
import { ISearchRequestReferrer } from '../../indigofair/data/ISearchRequestReferrer';

export namespace IProductFirstCatNavRequest {
  export enum SortBy {
    FEATURED = 'FEATURED',
    NEWEST = 'NEWEST',
  }

  export enum Placement {
    /**
     * Obeys filter keys, and returns filter sections (default if not specified).
     */
    MAIN_GRID = 'MAIN_GRID',
    /**
     * Skips building filter sections and limit brands that were first activated within last 30 days
     * @deprecated
     */
    NEW_BRAND_CAROUSEL = 'NEW_BRAND_CAROUSEL',
  }

  /**
   * Return an instance of IProductFirstCatNavRequest with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IProductFirstCatNavRequest>
  ): IProductFirstCatNavRequest =>
    ({
      filter_keys: [],
      previous_filter_keys: [],
      initial_request_exposed_visual_filter_option_keys: [],
      filters_to_include: [],
      ...(partial ?? {}),
    }) as IProductFirstCatNavRequest;
}

export interface IProductFirstCatNavRequest {
  /**
   * Path identifier of the category
   */
  category: string | undefined;
  pagination_data: IPaginationData | undefined;
  container_name: string | undefined;
  filter_keys: Array<string>;
  /**
   * Clients should provide the value of [filterKeys] used to make the last request to this endpoint.
   * The backend uses [filterKeys] and [previousFilterKeys] to deduce which filter keys were selected or deselected by
   * the user, then:
   *   - If a filter option with sub-options was (de)selected, all its sub-options will also be (de)selected.
   *   - If a filter option with a parent was selected, its parent will be marked as partially or fully selected,
   *     depending on the states of the parent's other sub-options.
   *   - If a filter option with a parent was deselected, its parent will be marked as partially selected or
   *     deselected, depending on the states of the parent's other sub-options.
   */
  previous_filter_keys: Array<string>;
  placement: keyof typeof IProductFirstCatNavRequest.Placement | undefined;
  /**
   * Clients should set this field to true if the request is the first the retailer is making for a specific category.
   * Subsequent requests for the same category with different filter keys should have this field set to false.
   * The backend might use this field to change the behaviour of the request, e.g. by auto-selecting all of the
   * promotions filter options during Faire Winter Market, so that retailers only see brands with promotions by default.
   * @deprecated
   */
  is_initial_request: boolean | undefined;
  /**
   * Fetch products created within 30 days.
   */
  return_only_new_products: boolean | undefined;
  /**
   * DEPRECATED - DO NOT USE. The preorder fetch logic is now based on the filters linked to the category, not this
   * field. Historically this was used to fetch only preorderable products (used by the preorder marketplace)
   * @deprecated
   */
  return_only_preorderable_products: boolean | undefined;
  /**
   * Currently only for mobile use
   * A string that combines and serializes all the flag fields in this request
   * So when new flags are added, mobile does not need to update request proto.
   * Flags needs to be deserialized and injected back into the request fields in BE.
   */
  json_serialized_request_fields: string | undefined;
  /**
   * List of FilterOption.key
   * This is the list of visual filters that we decided to expose during first request.
   * It should be empty if this is the first time the retailer is searching for a specific search query.
   * For requests after first request(the requests which retailer stays in the search query but applies a filter option),
   * BE won't fetch possible filter options from S3 file. BE validates the options from this list.
   * @deprecated
   */
  initial_request_exposed_visual_filter_option_keys: Array<string>;
  /**
   * Return retrieval and ranking debug info in the response (only for employees).
   */
  return_retrieval_and_ranking_debug_info: boolean | undefined;
  /**
   * Should we apply a post sorting on products based on retailers similar
   * brand token ranks?
   * @deprecated
   */
  apply_ranking_by_similar_brands: boolean | undefined;
  /**
   * Deprecated, to be removed in a follow up
   * @deprecated
   */
  return_promoted_placement_products: boolean | undefined;
  /**
   * Is request from European Brands page
   * @deprecated
   */
  is_european_brands: boolean | undefined;
  /**
   * Is request from Plus Size category page
   * @deprecated
   */
  is_shop_by_plus_size: boolean | undefined;
  /**
   * The component that referred the user to this search.
   * This is primarily tracked for experiment metrics and data science purposes
   */
  referrer: ISearchRequestReferrer | undefined;
  /**
   * This is the list of filters to include. If not specified, then all filters are returned.
   */
  filters_to_include: Array<string>;
  /**
   * This is the URL path that represents the category.
   */
  navigation_path: string | undefined;
}
